import React, { useState, useEffect } from 'react';
import { UserCheck, UserX, Clock, Mail, Phone, MapPin, Calendar, Package, Briefcase, CheckCircle, XCircle } from 'lucide-react';
import Button from '../components/common/Button';
import Alert from '../components/common/Alert';
import Loading from '../components/common/Loading';
import Modal from '../components/common/Modal';
import { formatCPF, formatPhone, formatDate, formatCurrency } from '../utils/formatters';
import { Plan } from '../types';
import api from '../services/api';

interface PendingAssociate {
  id: number;
  nome: string;
  cpf: string;
  email: string;
  telefone: string;
  celular?: string;
  data_nascimento: string;
  endereco: string;
  numero: string;
  complemento?: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
  created_at: string;
  email_verified_at: string;
  plan_id?: number;
  plan?: Plan;
  is_hospital_employee?: boolean;
  employee_cpf?: string;
  employee_registration?: string;
  employee_validated?: boolean | null;
}

const PendingApprovals: React.FC = () => {
  const [associates, setAssociates] = useState<PendingAssociate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedAssociate, setSelectedAssociate] = useState<PendingAssociate | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<number | undefined>(undefined);

  useEffect(() => {
    loadPendingAssociates();
    loadAvailablePlans();
  }, []);

  const loadPendingAssociates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/associates/pending-approvals');
      setAssociates(response.data);
    } catch (err: any) {
      setError('Erro ao carregar associados pendentes');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailablePlans = async () => {
    try {
      const response = await api.get('/plans/active');
      setAvailablePlans(response.data);
    } catch (err: any) {
      console.error('Erro ao carregar planos:', err);
    }
  };

  const handleApprove = async (id: number) => {
    // Encontrar o associado
    const associate = associates.find(a => a.id === id);

    // Verificar se o associado tem um plano
    if (!associate?.plan_id) {
      // Se não tem plano, abrir modal de seleção
      setSelectedAssociate(associate || null);
      setShowPlanModal(true);
      return;
    }

    // Se tem plano, aprovar diretamente
    await confirmApproval(id);
  };

  const confirmApproval = async (id: number, planId?: number) => {
    setProcessingId(id);
    try {
      const payload = planId ? { plan_id: planId } : {};
      await api.post(`/associates/${id}/approve`, payload);
      setSuccess('Associado aprovado com sucesso!');
      await loadPendingAssociates();
      setShowDetailsModal(false);
      setShowPlanModal(false);
      setSelectedPlanId(undefined);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao aprovar associado');
    } finally {
      setProcessingId(null);
    }
  };

  const handleApproveWithPlan = async () => {
    if (!selectedAssociate || !selectedPlanId) {
      setError('Por favor, selecione um plano');
      return;
    }

    await confirmApproval(selectedAssociate.id, selectedPlanId);
  };

  const handleReject = async () => {
    if (!selectedAssociate) return;

    setProcessingId(selectedAssociate.id);
    try {
      await api.post(`/associates/${selectedAssociate.id}/reject`, {
        rejection_reason: rejectReason,
      });
      setSuccess('Cadastro rejeitado');
      await loadPendingAssociates();
      setShowRejectModal(false);
      setShowDetailsModal(false);
      setRejectReason('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao rejeitar cadastro');
    } finally {
      setProcessingId(null);
    }
  };

  const openDetails = (associate: PendingAssociate) => {
    setSelectedAssociate(associate);
    setShowDetailsModal(true);
  };

  const openRejectModal = (associate: PendingAssociate) => {
    setSelectedAssociate(associate);
    setShowRejectModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Aprovações Pendentes
        </h1>
        <p className="text-gray-600">
          Analise e aprove os cadastros de novos associados
        </p>
      </div>

      {error && (
        <Alert variant="error" onClose={() => setError('')} className="mb-6">
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} className="mb-6">
          {success}
        </Alert>
      )}

      {associates.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Nenhuma aprovação pendente
          </h3>
          <p className="text-gray-600">
            Todos os cadastros foram processados
          </p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {associates.map((associate) => (
            <div
              key={associate.id}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200 p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {associate.nome}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {formatCPF(associate.cpf)}
                  </p>
                </div>
                <div className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-1 rounded">
                  Pendente
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Mail className="w-4 h-4" />
                  <span className="truncate">{associate.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone className="w-4 h-4" />
                  <span>{formatPhone(associate.telefone)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MapPin className="w-4 h-4" />
                  <span>{associate.cidade} - {associate.estado}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>Cadastro: {formatDate(associate.created_at)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Package className="w-4 h-4" />
                  {associate.plan_id ? (
                    <span className="text-green-600 font-medium">
                      {associate.plan?.nome || 'Plano selecionado'}
                    </span>
                  ) : (
                    <span className="text-orange-600 font-medium">
                      Sem plano
                    </span>
                  )}
                </div>
                {!!associate.is_hospital_employee && (
                  <div className="flex items-center gap-2 text-sm">
                    <Briefcase className="w-4 h-4 text-blue-600" />
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600 font-medium">
                        Funcionário do Hospital
                      </span>
                      {associate.employee_validated === true && (
                        <span className="flex items-center gap-1 text-green-600 text-xs font-semibold">
                          <CheckCircle className="w-3 h-3" />
                          Validado
                        </span>
                      )}
                      {associate.employee_validated === false && (
                        <span className="flex items-center gap-1 text-red-600 text-xs font-semibold">
                          <XCircle className="w-3 h-3" />
                          Não Validado
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => openDetails(associate)}
                  className="flex-1"
                >
                  Detalhes
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleApprove(associate.id)}
                  loading={processingId === associate.id}
                  disabled={processingId === associate.id}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <UserCheck className="w-4 h-4 mr-1" />
                  Aprovar
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Detalhes */}
      {showDetailsModal && selectedAssociate && (
        <Modal
          isOpen={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          title="Detalhes do Associado"
        >
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Informações Pessoais</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-600">Nome:</span>
                  <p className="font-medium">{selectedAssociate.nome}</p>
                </div>
                <div>
                  <span className="text-gray-600">CPF:</span>
                  <p className="font-medium">{formatCPF(selectedAssociate.cpf)}</p>
                </div>
                <div>
                  <span className="text-gray-600">Data de Nascimento:</span>
                  <p className="font-medium">{formatDate(selectedAssociate.data_nascimento)}</p>
                </div>
                <div>
                  <span className="text-gray-600">E-mail:</span>
                  <p className="font-medium">{selectedAssociate.email}</p>
                </div>
                <div>
                  <span className="text-gray-600">Telefone:</span>
                  <p className="font-medium">{formatPhone(selectedAssociate.telefone)}</p>
                </div>
                {selectedAssociate.celular && (
                  <div>
                    <span className="text-gray-600">Celular:</span>
                    <p className="font-medium">{formatPhone(selectedAssociate.celular)}</p>
                  </div>
                )}
              </div>
            </div>

            {!!selectedAssociate.is_hospital_employee && (
              <div className={`border rounded-lg p-4 ${
                selectedAssociate.employee_validated === true
                  ? 'bg-green-50 border-green-200'
                  : selectedAssociate.employee_validated === false
                    ? 'bg-red-50 border-red-200'
                    : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-start gap-3">
                  <Briefcase className={`w-5 h-5 mt-0.5 ${
                    selectedAssociate.employee_validated === true
                      ? 'text-green-600'
                      : selectedAssociate.employee_validated === false
                        ? 'text-red-600'
                        : 'text-blue-600'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <p className={`text-sm font-semibold ${
                        selectedAssociate.employee_validated === true
                          ? 'text-green-900'
                          : selectedAssociate.employee_validated === false
                            ? 'text-red-900'
                            : 'text-blue-900'
                      }`}>
                        Funcionário do Hospital
                      </p>
                      {selectedAssociate.employee_validated === true && (
                        <span className="flex items-center gap-1 text-green-600 text-xs font-bold bg-green-100 px-2 py-1 rounded">
                          <CheckCircle className="w-3 h-3" />
                          VALIDADO
                        </span>
                      )}
                      {selectedAssociate.employee_validated === false && (
                        <span className="flex items-center gap-1 text-red-600 text-xs font-bold bg-red-100 px-2 py-1 rounded">
                          <XCircle className="w-3 h-3" />
                          NÃO VALIDADO
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {selectedAssociate.employee_cpf && (
                        <div>
                          <span className={
                            selectedAssociate.employee_validated === true
                              ? 'text-green-700'
                              : selectedAssociate.employee_validated === false
                                ? 'text-red-700'
                                : 'text-blue-700'
                          }>CPF Funcional:</span>
                          <p className={`font-medium ${
                            selectedAssociate.employee_validated === true
                              ? 'text-green-900'
                              : selectedAssociate.employee_validated === false
                                ? 'text-red-900'
                                : 'text-blue-900'
                          }`}>{formatCPF(selectedAssociate.employee_cpf)}</p>
                        </div>
                      )}
                      {selectedAssociate.employee_registration && (
                        <div>
                          <span className={
                            selectedAssociate.employee_validated === true
                              ? 'text-green-700'
                              : selectedAssociate.employee_validated === false
                                ? 'text-red-700'
                                : 'text-blue-700'
                          }>Matrícula:</span>
                          <p className={`font-medium ${
                            selectedAssociate.employee_validated === true
                              ? 'text-green-900'
                              : selectedAssociate.employee_validated === false
                                ? 'text-red-900'
                                : 'text-blue-900'
                          }`}>{selectedAssociate.employee_registration}</p>
                        </div>
                      )}
                    </div>
                    {selectedAssociate.employee_validated === false && (
                      <div className="mt-3 p-2 bg-red-100 border border-red-300 rounded text-xs text-red-800">
                        <strong>Atenção:</strong> Os dados informados não correspondem a nenhum funcionário ativo no sistema.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Endereço</h4>
              <div className="text-sm">
                <p className="font-medium">
                  {selectedAssociate.endereco}, {selectedAssociate.numero}
                  {selectedAssociate.complemento && ` - ${selectedAssociate.complemento}`}
                </p>
                <p className="text-gray-600">
                  {selectedAssociate.bairro} - {selectedAssociate.cidade}/{selectedAssociate.estado}
                </p>
                <p className="text-gray-600">CEP: {selectedAssociate.cep}</p>
              </div>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>E-mail verificado em:</strong>{' '}
                {formatDate(selectedAssociate.email_verified_at)}
              </p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Plano</h4>
              {selectedAssociate.plan_id ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-green-600" />
                    <p className="text-sm text-green-900 font-medium">
                      {selectedAssociate.plan?.nome || 'Plano selecionado'}
                      {selectedAssociate.plan?.valor && (
                        <span className="ml-2 text-green-700">
                          - {formatCurrency(selectedAssociate.plan.valor)}/mês
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-orange-600" />
                    <p className="text-sm text-orange-900 font-medium">
                      Nenhum plano selecionado
                    </p>
                  </div>
                  <p className="text-xs text-orange-700 mt-1">
                    Um plano será solicitado ao aprovar este cadastro
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="danger"
                onClick={() => {
                  setShowDetailsModal(false);
                  openRejectModal(selectedAssociate);
                }}
                className="flex-1"
              >
                <UserX className="w-4 h-4 mr-2" />
                Rejeitar
              </Button>
              <Button
                onClick={() => handleApprove(selectedAssociate.id)}
                loading={processingId === selectedAssociate.id}
                disabled={processingId === selectedAssociate.id}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Aprovar
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal de Rejeição */}
      {showRejectModal && selectedAssociate && (
        <Modal
          isOpen={showRejectModal}
          onClose={() => {
            setShowRejectModal(false);
            setRejectReason('');
          }}
          title="Rejeitar Cadastro"
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              Tem certeza que deseja rejeitar o cadastro de{' '}
              <strong>{selectedAssociate.nome}</strong>?
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motivo da rejeição (opcional)
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Digite o motivo da rejeição..."
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleReject}
                loading={processingId === selectedAssociate.id}
                disabled={processingId === selectedAssociate.id}
                className="flex-1"
              >
                Confirmar Rejeição
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal de Seleção de Plano */}
      {showPlanModal && selectedAssociate && (
        <Modal
          isOpen={showPlanModal}
          onClose={() => {
            setShowPlanModal(false);
            setSelectedPlanId(undefined);
          }}
          title="Selecionar Plano para Aprovação"
        >
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <Package className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-900">
                    Plano obrigatório para aprovação
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    O associado <strong>{selectedAssociate.nome}</strong> não possui um plano vinculado.
                    Selecione um plano antes de aprovar o cadastro.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selecione o Plano *
              </label>
              <select
                value={selectedPlanId || ''}
                onChange={(e) => setSelectedPlanId(e.target.value ? Number(e.target.value) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Escolha um plano...</option>
                {availablePlans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.nome} - {formatCurrency(plan.valor)}/mês
                  </option>
                ))}
              </select>
            </div>

            {availablePlans.length === 0 && (
              <Alert variant="warning">
                Nenhum plano ativo disponível. Configure planos no sistema antes de aprovar associados.
              </Alert>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowPlanModal(false);
                  setSelectedPlanId(undefined);
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleApproveWithPlan}
                loading={processingId === selectedAssociate.id}
                disabled={processingId === selectedAssociate.id || !selectedPlanId || availablePlans.length === 0}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Aprovar com Plano
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PendingApprovals;
